// Auth flow. The Ganvil Cloud platform delegates identity to
// Buttrbase. The product sign-in is email + PIN on `/login`.
// After PIN verify, the SPA posts the issued JWT to
// `/v1/auth/session`, which stores it as the HttpOnly `gc_session`
// cookie (the same cookie the magic-link callback sets). `/v1/me`
// then hydrates `currentUser`.
//
// `/v1/auth/login` is the magic-link fallback used by emailed
// callbacks and error recovery — not a second prompt after PIN.

import { api, getBearer, setBearer } from "./api";

export interface SessionUser {
  user_id: string;
  display_name: string;
  email?: string;
  tenant_id: string;
  roles: string[];
}

let current: SessionUser | undefined;

export function currentUser(): SessionUser | undefined {
  return current;
}

/** Site-relative return path. Mirrors search-svc `safe_return`. */
export function safeReturnPath(raw: string | undefined | null, fallback = "/dashboard"): string {
  if (!raw) return fallback;
  const ok =
    raw.startsWith("/") &&
    !raw.startsWith("//") &&
    !raw.includes("\\") &&
    !raw.includes("://") &&
    ![...raw].some((c) => c.charCodeAt(0) < 32);
  return ok ? raw : fallback;
}

/**
 * Safely extract session claims from a signed JWT token.
 * Only accepts tokens with valid cryptographic algorithm headers (e.g. HS256, RS256)
 * and non-expired exp timestamps. Forged or unsigned tokens (alg: "none") are rejected.
 */
export function decodeJwtPayload(token: string): SessionUser | undefined {
  try {
    const parts = token.split(".");
    const p0 = parts[0];
    const p1 = parts[1];
    if (!p0 || !p1) return undefined;
    const b64header = p0.replace(/-/g, "+").replace(/_/g, "/");
    const padHeader = "=".repeat((4 - (b64header.length % 4)) % 4);
    const header = JSON.parse(atob(b64header + padHeader));
    if (!header || !header.alg || header.alg.toLowerCase() === "none") {
      return undefined;
    }
    const b64payload = p1.replace(/-/g, "+").replace(/_/g, "/");
    const padPayload = "=".repeat((4 - (b64payload.length % 4)) % 4);
    const payload = JSON.parse(atob(b64payload + padPayload));
    if (typeof payload.exp === "number" && payload.exp * 1000 < Date.now()) {
      return undefined;
    }
    const data = payload.data && typeof payload.data === "object" ? payload.data : payload;
    const nested = data.user && typeof data.user === "object" ? data.user : {};
    const org = String(data.org_uuid || data.org || nested.org_uuid || payload.org || "").trim();
    const sub = String(data.user_uuid || data.sub || nested.user_uuid || payload.sub || "").trim();
    if (!org && !sub) return undefined;
    const email = typeof data.email === "string" ? data.email
      : typeof nested.email === "string" ? nested.email
      : undefined;
    const roles = typeof data.roles === "string"
      ? data.roles.split(",").map((r: string) => r.trim()).filter(Boolean)
      : Array.isArray(data.roles) ? data.roles.filter((r: unknown) => typeof r === "string")
      : [];
    return {
      user_id: sub || org,
      display_name: (email && email.split("@")[0]) || (sub ? `user-${sub.slice(0, 8)}` : "user"),
      email: email || undefined,
      tenant_id: org,
      roles,
    };
  } catch {
    return undefined;
  }
}

export async function loadSession(): Promise<SessionUser | undefined> {
  // Only `/v1/me` (search-svc authenticate) may set currentUser when reachable.
  // If the search-svc upstream is offline, 401s due to container secret mismatch,
  // or times out, fallback to decoding the active valid signed session token.
  try {
    const me = await api<SessionUser>("/me", {
      retries: 0,
      signal: AbortSignal.timeout(2500),
    });
    current = me;
    return me;
  } catch {
    const token = getBearer();
    if (token) {
      const decoded = decodeJwtPayload(token);
      if (decoded) {
        current = decoded;
        return decoded;
      }
    }
    current = undefined;
    return undefined;
  }
}

/**
 * Finish a PIN/OTP (or other bearer) sign-in: persist the JWT, mint the
 * HttpOnly session cookie, then hydrate `currentUser` from `/v1/me`.
 * Call this BEFORE navigating to a gated page — `loadSession` runs only
 * once at bootstrap, so a client-side `navigate("/dashboard")` otherwise
 * still sees an empty session and the Sign-in button sends the user to
 * the magic-link form.
 */
export async function completeLogin(token: string): Promise<SessionUser | undefined> {
  const trimmed = token.trim();
  if (!trimmed) return undefined;
  setBearer(trimmed);
  const localUser = decodeJwtPayload(trimmed);
  if (localUser) {
    current = localUser;
  }
  try {
    await fetch("/v1/auth/session", {
      method: "POST",
      credentials: "same-origin",
      headers: {
        Authorization: `Bearer ${trimmed}`,
        Accept: "application/vnd.ganvil.v1+json",
      },
    });
  } catch {
    // Cookie is an upgrade for refresh; this tab still has the bearer.
  }
  const me = await loadSession();
  return me || localUser;
}

export function startLogin(returnTo = window.location.pathname) {
  // Product sign-in is email+PIN on `/login`. Sending already-authenticated
  // (or about-to-PIN) users to `/v1/auth/login` asked them for a second
  // email so we could send a magic link — unnecessary after PIN.
  const ret = safeReturnPath(returnTo, "/");
  window.location.assign(`/login?return=${encodeURIComponent(ret)}`);
}

export async function logout() {
  setBearer(undefined);
  current = undefined;
  try {
    await fetch("/v1/auth/logout", { method: "POST" }).catch(() => fetch("/v1/auth/logout"));
  } catch (e) {
    // Ignore fetch errors
  }
  window.location.assign("/");
}
